import { TranslateContext } from '@denysvuika/preact-translate';
import { Position2D } from '@digital-bridge/fine';
import * as AR from '@dora/arctic';
import { ModaSofa, Price } from '@dora/backpack';
import {
    AddItemsDrawer,
    DrawerWithCta,
    Fab,
    SceneColourFab as GlobalColourFab,
    SceneFloorSizeFab,
    TopBarWithButtons,
} from '@dora/components';
import { appBarHeight, fabPadding, Platform, toPixels } from '@dora/components/src/sizing';
import { Global } from '@emotion/react';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import QueuePlayNextIcon from '@mui/icons-material/QueuePlayNext';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/system/Box';
import * as E from 'fp-ts/Either';
import { identity, pipe } from 'fp-ts/function';
import * as O from 'fp-ts/Option';
import Preact from 'preact';
import * as Hooks from 'preact/hooks';
import { resolveItemBundles } from '../data/bundling';
import * as CatalogueEntry from '../data/catalogueEntry';
import * as Pose from '../data/pose';
import * as PC from '../data/productCatalogue';
import * as ProductSetLayout from '../data/productSetLayout';
import * as Room from '../data/room';
import * as Layout from '../data/shareLayout';
import * as RoomFixtures from '../fixtures/room';
import * as Logging from '../store/actions/logging';
import * as Navigation from '../store/actions/navigation';
import * as CoreStore from '../store/core';
import * as LoadingStore from '../store/loading';
import { Product } from '../data/product';
import * as Selectors from '../store/selectors';
import * as Alert from './alert';
import * as PersistentAlert from './persistentAlert';
import { ArRunway, ArView, ModelRequest, ModelRequestEntry } from './ar';
import * as Arya from './arya';
import * as CheckoutDrawer from './CheckoutDrawer';
import * as ConfigureModaSofa from './configureModaSofa';
import * as ExitScreen from './exitModal';
import * as GlobalColourDrawer from './globalColourDrawer';
import { useDispatch, useSelector } from './hooks/redux';
import { useAppControl } from './hooks/useAppControl';
import { useFurnitureList } from './hooks/useFurnitureList';
import { useLoadCatalogue } from './hooks/useGetCatalogue';
import { useGetGlobalConfigurableProperties } from './hooks/useGetConfigurableProperties';
import { useLoadProductData } from './hooks/useGetProductData';
import { useGetSofaTemplates } from './hooks/useGetSofaTemplates';
import { useInitialLayout } from './hooks/useInitialLayout';
import { useLoadingState } from './hooks/useLoadingState';
import { useOpenable, useOpenableWithData, useOpenableWithMessage } from './hooks/useOpenable';
import { Cart, CheckoutLocation } from './hooks/useParentAPI';
import { useSaveLinkState } from './hooks/useSaveLinkState';
import * as DrawerCtaContentBasket from './O_DrawerCtaContentBasket';
import * as ProductSetGlbAutomator from './productSetGlbAutomator';
import * as SceneItemContextMenu from './sceneItemContextMenu';
import * as DesktopAddItemButton from './sceneView/desktopAddItemButton';
import { getBasketData } from './sceneView/getBasketData';
import * as LocalHooks from './sceneView/hooks';
import { useCommands as useConsoleCommands } from './sceneView/hooks/commands';
import { useTrackingForLoadingComplete } from './sceneView/hooks/useTrackingForLoadingComplete';
import * as MobileAddItemButton from './sceneView/mobileAddItemButton';
import * as Loading from './sceneView/sceneLoadingModal';
import * as Share from './share';
import * as SpaceDimensionsDrawer from './spaceDimensionsDrawer';

export { LocalHooks as Hooks };

export type Props = {
    environment: 'development' | 'production';
    platform: Platform;
    portrait: boolean;
    proceedToCart: (cart: Cart, from: CheckoutLocation) => void;
    copyToClipboard: (text: string) => void;
    currentUrl: string;
    toggleModal: (modal: boolean) => void;
};

/**
 * Display the Arya Scene, along with a Contextual Drawer
 */
export function Component(props: Props) {
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);
    const launchData = useSelector(Selectors.Core.launchData);
    const theme = useTheme();
    const [aryaRef, setAryaRef] = Hooks.useState<O.Option<Arya.Interface>>(O.none);
    const [spaceDimensionsUpdating, setSpaceDimensionsUpdating] = Hooks.useState<boolean>(false);
    const [sofaLoading, setSofaLoading] = Hooks.useState<boolean>(false);
    const appControl = useAppControl();
    const [_layoutMode, setLayoutMode] = Hooks.useState(false);
    // TODO: create command to show 'hero' Product SKUs in catalogue cards

    const { t } = Hooks.useContext(TranslateContext);
    // Kept separately from `useDrawerState` because the AR Runway takes over the whole view - it is not a drawer.
    // Unfortunately, the AR Runway cannot be lifted to the level of the root component as it relies on Arya.
    const spaceArRunway = useOpenable();

    Hooks.useEffect(() => {
        if (launchData?.ar) {
            spaceArRunway.open();
        }
    }, [launchData?.ar]);

    const sceneLoading = useLoadingState();

    useLoadCatalogue();
    useLoadProductData();
    useGetSofaTemplates();

    const conversionId = useSelector(Selectors.ConversionId.conversionId);
    const isModularSofa = useSelector(Selectors.FurnitureList.checkHasConfiguration);
    const findConfiguration = useSelector(Selectors.FurnitureList.findConfiguration);
    const sofaTemplates = useSelector(Selectors.ModaSofa.templates);

    // We can use the presence of (or lack of) sofa templates to indicate support for modular sofas
    // in the current range.
    const rangeSupportsModularSofas = useSelector(Selectors.ModaSofa.currentRangeHasTemplates);
    const range = useSelector(Selectors.Core.range);

    /**
     * Alerts disappear after ~3s, while persistent Alerts require the user to dismiss them.
     */
    const alert = useOpenableWithMessage();
    const persistentAlert = useOpenableWithMessage();

    const currentGlobalConfigurablePropertySelections = useSelector(
        Selectors.ConfigurableProperties.currentPropertySelections,
    );

    Hooks.useEffect(() => {
        const arya = O.toNullable(aryaRef);
        appControl.enhanceCloseApp('sceneView_reset', () => {
            sceneDrawer.viewActions.toSceneList();
            spaceArRunway.close();
            dispatch(CoreStore.requireLoading());
            arya?.clearScene();
            arya?.resetCamera();
            arya?.updateRoomDimensions(RoomFixtures.basic);
            dispatch(LoadingStore.clearLoading());
            dispatch(CoreStore.clearSpace());
        });
    }, [aryaRef, appControl]);

    const bundles = useSelector(Selectors.ProductData.bundlesForRange);
    const productBySku = useSelector(Selectors.ProductData.productBySkuFn);
    const furnitureList = useFurnitureList(aryaRef);

    const { sharingUrlRequest, generateNewSharingUrl } = useSaveLinkState(() => {
        return Layout.generate(
            aryaRef,
            furnitureList,
            range ?? '',
            O.toNullable(aryaRef)?.roomDimensions() || RoomFixtures.basic,
            currentGlobalConfigurablePropertySelections,
        );
    }, props.currentUrl);

    // TODO: replace this with something that makes sense in the new multi-drawer world.
    const sceneDrawer = LocalHooks.useSceneDrawerState(generateNewSharingUrl);

    const itemBundling = Hooks.useMemo(
        () =>
            resolveItemBundles(
                sku => productBySku?.(sku)?.price || 0,
                bundles ?? [],
                furnitureList,
            ),
        [bundles, furnitureList, productBySku],
    );

    const { singleItemsPrice, originalTotal, totalPrice, productIdList, cart } = getBasketData(
        itemBundling.singleItems,
        itemBundling.bundles,
    );

    useTrackingForLoadingComplete(sceneLoading, itemBundling);

    // console commands allow developers to control the application from the developer console using `fixtuur.list()`.
    // See ticket TDC-875 and Dora ADR 002 for more details.
    useConsoleCommands(aryaRef, itemBundling, furnitureList);

    useInitialLayout(
        aryaRef,
        () => {
            sceneDrawer.viewActions.reset();
        },
        (msg: string) => {
            persistentAlert.open(msg);
        },
    );

    const catalogueEntriesForCurrentRange = pipe(
        useSelector(Selectors.Catalogue.catalogueEntriesForCurrentRangeWithoutModularSofaPieces),
        E.getOrElse(() => [] as Array<CatalogueEntry.CatalogueEntry>),
    );

    useGetGlobalConfigurableProperties();
    const setContainsConfigurableProperties = useSelector(
        Selectors.ConfigurableProperties.setContainsConfigurableProperties,
    );

    const { loadingFurnitureName, setLoadingFurnitureId, setLoadingFurnitureName } =
        LocalHooks.useFurnitureLoadingState(aryaRef, alert);

    const { loading: colourLoading, unavailable } = LocalHooks.useUpdateFurnitureInScene(
        aryaRef,
        catalogueEntriesForCurrentRange,
    );

    const catalogueEntriesByConfigurableProperties = pipe(
        useSelector(Selectors.Catalogue.catalogueEntriesForGlobalPropertySelections),
        E.getOrElse(() => [] as Array<CatalogueEntry.CatalogueEntry>),
    );

    const catalogueEntries = setContainsConfigurableProperties
        ? catalogueEntriesByConfigurableProperties
        : catalogueEntriesForCurrentRange;

    const dialog = useOpenableWithData<{ id: string; from: Navigation.ConfigureButtonLocation }>();
    const isDesktop = props.platform === Platform.DESKTOP;

    const arCompatibility = useSelector(Selectors.Core.arCompatibility);

    const selectedFurniture = LocalHooks.useSelectedState(aryaRef);

    const [automaticGlbDownloaderVisible, setAutomaticGlbDownloaderVisible] = Hooks.useState(false);

    const dispatch = useDispatch();

    // show fabs: when scene.currentView === 'scene-list' && !selectedFurniture
    // show contextMenu: when scene.currentView === 'scene-list' && selectedFurniture
    const [showFabs, setShowFabs] = Hooks.useState(false);
    const [showContextMenu, setShowContextMenu] = Hooks.useState(false);

    Hooks.useEffect(() => {
        setShowFabs(sceneDrawer.currentView === 'scene-list' && selectedFurniture === undefined);
        setShowContextMenu(
            sceneDrawer.currentView === 'scene-list' &&
                selectedFurniture !== undefined &&
                loadingFurnitureName === '',
        );
    }, [sceneDrawer.currentView, selectedFurniture, loadingFurnitureName]);

    function handleDeleteFurniture(data: { id: string; from: Navigation.ConfigureButtonLocation }) {
        const { id, from } = data;

        const trackModularSofaDelete = () => {
            if (!sofaTemplates) {
                return;
            }
            const productIds = pipe(
                id,
                findConfiguration,
                O.map(configuration =>
                    ModaSofa.placePieces(sofaTemplates[configuration.templateKey])(
                        configuration.size,
                        configuration.interstitialPlacements,
                        configuration.cornerStyle,
                    ),
                ),
                O.map(pieces => pieces.map(p => p.sku)),
                O.fold(() => [], identity),
            );

            dispatch(Navigation.removedFurnitureAction({ productIds, articles: ['sofa'], from }));
        };

        const trackFurnitureDelete = () => {
            const articles =
                O.toNullable(aryaRef)
                    ?.furnitureSummaries()
                    .filter(f => f.persistentID === id)
                    .map(f => f.articles.map(a => a.type))
                    .reduce((acc, cur) => acc.concat(cur), []) ?? [];

            const productIds =
                O.toNullable(
                    pipe(
                        aryaRef,
                        O.chain(a => a.furnitureProducts(id)),
                    ),
                ) ?? [];

            dispatch(Navigation.removedFurnitureAction({ productIds, articles, from }));
        };

        if (isModularSofa(id)) {
            trackModularSofaDelete();
            O.toNullable(aryaRef)?.removeModularSofa(id);
        } else {
            trackFurnitureDelete();
            O.toNullable(aryaRef)?.removeFurniture(id);
        }

        alert.open('Item Removed');
    }

    function handleSelectConfigureFurniture(id: string) {
        pipe(
            findConfiguration(id),
            O.map(configuration => {
                !sceneDrawer.open && sceneDrawer.toggleOpen();
                sceneDrawer.viewActions.toConfigure(id, configuration);
            }),
        );
    }

    function toBasket() {
        dispatch(
            Navigation.finaliseAction({
                products: cart,
                value: totalPrice,
            }),
        );
        sceneDrawer.viewActions.toBasket();
    }

    const locale = useSelector(Selectors.Core.locale);
    const currency = useSelector(Selectors.Core.currency);

    // For uncached models, the loading seems to trigger a little to fast.
    // This can lead to furniture all being a bit 'muddled' in the exported GLTF.
    //
    // This timeout is set to avoid this problem and give Arya a chance to position
    // everything, though it should be fixed 'properly' next chance we get.
    const [modelLoadDelay, setModelLoadDelay] = Hooks.useState(true);
    Hooks.useEffect(() => {
        const timer = setTimeout(() => {
            setModelLoadDelay(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, [aryaRef]);

    const enterAr =
        arCompatibility === AR.Compatibility.Compatible
            ? () => {
                  sceneDrawer.viewActions.reset();
                  spaceArRunway.open();
                  props.toggleModal(true);
              }
            : arCompatibility === AR.Compatibility.IncompatibleDesktop
            ? sceneDrawer.viewActions.toArMobileQr
            : undefined;

    return (
        <>
            {spaceArRunway.isOpen && (
                <ArRunway.Component
                    onReturn={() => {
                        spaceArRunway.close();
                        props.toggleModal(false);
                    }}
                    getGltfBlob={() => {
                        return pipe(
                            aryaRef,
                            O.filter(() => !sceneLoading),
                            O.filter(() => !modelLoadDelay),
                            O.map(arya => arya.generateFurnitureGLTF()),
                            O.map(async gltf => {
                                const blob = await gltf;
                                return new Blob([blob], {
                                    type: 'model/gltf-binary',
                                });
                            }),
                        );
                    }}
                    getModelRequest={() => {
                        return pipe(
                            aryaRef,
                            O.filter(() => !sceneLoading),
                            O.filter(() => !modelLoadDelay),
                            O.map(
                                arya =>
                                    ({
                                        models: arya
                                            .furnitureSummaries()
                                            .map(f =>
                                                f.articles.map(article => ({
                                                    assetId: `${article.canonicalID}-${article.type}`,
                                                    position: {
                                                        x: article.position.x(),
                                                        y: article.position.y(),
                                                        z: article.position.z(),
                                                    },
                                                    orientation: {
                                                        w: article.orientation.quaternion()[0],
                                                        x: article.orientation.quaternion()[1],
                                                        y: article.orientation.quaternion()[2],
                                                        z: article.orientation.quaternion()[3],
                                                    },
                                                })),
                                            )
                                            .reduce(
                                                (acc, cur) => acc.concat(cur),
                                                [] as ModelRequestEntry[],
                                            ),
                                    } as ModelRequest),
                            ),
                        );
                    }}
                    returnToPlanner={t('i18n.space.returnToPlanner')}
                    placement="floor"
                />
            )}
            <Global
                styles={{
                    '.MuiDrawer-root > .MuiPaper-root': {
                        overflow: 'visible',
                    },
                }}
            />

            <Loading.Component open={sceneLoading} />
            <ExitScreen.Component
                open={sceneDrawer.currentView === 'exit-screen'}
                onCancel={() => {
                    sceneDrawer.viewActions.reset();
                }}
                size={isDesktop ? 'large' : 'medium'}
                copyToClipboard={text => {
                    dispatch(Navigation.shareLinkCopiedAction());
                    props.copyToClipboard(text);
                }}
                sharingUrlRequest={sharingUrlRequest}
                primaryCtaText={t('i18n.space.exitPrimaryCta')}
                secondaryCtaText={t('i18n.global.exitSecondaryCta')}
                titleText={t('i18n.space.exitTitle')}
                linkError={t('i18n.global.linkError')}
                linkHelperText={t('i18n.global.linkHelper')}
                linkLoading={t('i18n.global.linkLoading')}
                linkCtaText={t('i18n.global.linkCta')}
            />
            <Box
                height="100%"
                width="100%"
                sx={{
                    display: spaceArRunway.isOpen ? 'none' : 'grid',
                    // Row 0 is for the app bar
                    // Row 1 is for the scene
                    gridTemplateRows: `${appBarHeight}px 1fr`,
                }}
            >
                <TopBarWithButtons.Component
                    onCloseClick={() => {
                        sceneDrawer.viewActions.toExitScreen();
                    }}
                    total={Price.toLocaleString(totalPrice, locale, currency)}
                    onBasketClick={() => {
                        toBasket();
                    }}
                    onArClick={enterAr}
                    onSaveClick={() => {
                        sceneDrawer.viewActions.toShare();
                    }}
                    disabled={false}
                />
                <Box width="100vw" height={`calc(100vh - ${appBarHeight}px)`}>
                    <Alert.Component
                        show={alert.isOpen}
                        message={alert.message}
                        close={alert.close}
                        width="100%"
                        paddingTop={appBarHeight}
                    />
                    <PersistentAlert.Component
                        show={persistentAlert.isOpen}
                        message={persistentAlert.message}
                        close={persistentAlert.close}
                        width="100%"
                        paddingTop={appBarHeight}
                    />

                    <Box width="100%" height="100%" position="relative">
                        {/* Only show item context menus on furniture list view and catalogue view */}
                        {!sceneDrawer.fullHeightView &&
                            (sceneDrawer.currentView === 'scene-list' ||
                                sceneDrawer.currentView === 'catalogue') && (
                                <SceneItemContextMenu.Component
                                    bottomPadding={
                                        isDesktop
                                            ? fabPadding.desktopY
                                            : sceneDrawer.open
                                            ? fabPadding.mobileY
                                            : 32
                                    }
                                    size={isDesktop ? 'large' : 'small'}
                                    show={showContextMenu}
                                    showConfigure={
                                        O.toNullable(findConfiguration(selectedFurniture ?? '')) !==
                                        null
                                    }
                                    onConfigure={() =>
                                        selectedFurniture &&
                                        handleSelectConfigureFurniture(selectedFurniture)
                                    }
                                    onDelete={(from: Navigation.ConfigureButtonLocation) =>
                                        selectedFurniture &&
                                        handleDeleteFurniture({ id: selectedFurniture, from })
                                    }
                                />
                            )}

                        {/** Button Bar for Dev-only Features */}
                        {props.environment === 'development' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: isDesktop ? fabPadding.desktopY : fabPadding.mobileY,
                                    left: isDesktop ? fabPadding.desktopX : fabPadding.mobileX,
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gridGap: '8px',
                                }}
                            >
                                {/** Show a toggle button for switching the camera between 3D and 2D in development mode */}
                                <Fab.Component
                                    onClick={() =>
                                        setLayoutMode(prev => {
                                            if (prev) {
                                                O.toNullable(aryaRef)?.toggleViewMode('3D');
                                            } else {
                                                O.toNullable(aryaRef)?.toggleViewMode('2D');
                                            }
                                            return !prev;
                                        })
                                    }
                                    color="secondary"
                                    size={isDesktop ? 'large' : 'small'}
                                >
                                    <CameraswitchIcon />
                                </Fab.Component>
                                <Fab.Component
                                    color="secondary"
                                    size={isDesktop ? 'large' : 'small'}
                                    title="Automatic GLB Downloader"
                                    onClick={() => setAutomaticGlbDownloaderVisible(val => !val)}
                                >
                                    <QueuePlayNextIcon />
                                </Fab.Component>
                                <Fab.Component
                                    color="secondary"
                                    size={isDesktop ? 'large' : 'small'}
                                    title="Export Layout"
                                    onClick={() => {
                                        const setLayout = ProductSetLayout.generate(
                                            aryaRef,
                                            furnitureList,
                                            itemBundling.bundles[0]?.canonicalId,
                                            currentGlobalConfigurablePropertySelections,
                                        );
                                        // not using the logging system to avoid it being turned off at the wrong log level
                                        console.info(JSON.stringify(setLayout, null, 2));
                                        props.copyToClipboard(JSON.stringify(setLayout, null, 2));
                                        console.log('Copied to clipboard');
                                    }}
                                >
                                    <QueuePlayNextIcon />
                                </Fab.Component>
                            </Box>
                        )}
                        <Arya.Component
                            ref={(instance: O.Option<Arya.Interface> | null) => {
                                instance && setAryaRef(instance);
                            }}
                            showDebugTools={props.environment === 'development'}
                            environmentMap={
                                tenantIdentifier === 'moda'
                                    ? {
                                          path: 'assets/',
                                          lightEnvMapFilename: 'lightEnvMap.hdr',
                                          skyEnvMapFilename: 'skyEnvMap.png',
                                      }
                                    : undefined
                            }
                        />
                        <Box
                            display="flex"
                            flexDirection="column"
                            sx={{
                                position: 'absolute',
                                bottom: fabPadding.desktopY,
                                left: fabPadding.desktopX,
                            }}
                        >
                            <SceneFloorSizeFab.Component
                                onClick={() => {
                                    dispatch(Navigation.floorSizeChangeStartAction());
                                    sceneDrawer.viewActions.toFloorSize();
                                }}
                                show={showFabs}
                                size={
                                    props.platform === Platform.MOBILE && props.portrait
                                        ? 'medium'
                                        : 'large'
                                }
                            />
                            {setContainsConfigurableProperties && (
                                <GlobalColourFab.Component
                                    onClick={() => {
                                        sceneDrawer.viewActions.toGlobalColourConfig();
                                    }}
                                    sx={{ marginTop: `${fabPadding.desktopY}px` }}
                                    show={showFabs}
                                    size={
                                        props.platform === Platform.MOBILE && props.portrait
                                            ? 'medium'
                                            : 'large'
                                    }
                                />
                            )}
                        </Box>
                        {props.platform === Platform.MOBILE && props.portrait ? (
                            <MobileAddItemButton.Component
                                onAddClick={() => {
                                    sceneDrawer.viewActions.toCatalogue();
                                }}
                                show={showFabs}
                            />
                        ) : (
                            <DesktopAddItemButton.Component
                                onAddClick={() => {
                                    sceneDrawer.viewActions.toCatalogue();
                                }}
                                show={showFabs}
                            />
                        )}
                    </Box>
                </Box>
                {/** These Drawers pop up when needed and remain hidden otherwise. They should be
                 * pulled out into their own components */}
                <Share.Component
                    platform={props.platform}
                    portrait={props.portrait}
                    open={sceneDrawer.currentView === 'share'}
                    onClose={() => sceneDrawer.viewActions.reset()}
                    sx={{ zIndex: theme.zIndex.appBar + 1 }}
                    copyToClipboard={text => {
                        dispatch(Navigation.shareLinkCopiedAction());
                        props.copyToClipboard(text);
                    }}
                    sharingUrlRequest={sharingUrlRequest}
                />
                <DrawerWithCta.Component
                    platform={props.platform}
                    portrait={props.portrait}
                    open={sceneDrawer.currentView === 'basket'}
                    onClose={sceneDrawer.viewActions.reset}
                    sx={{ zIndex: theme.zIndex.appBar + 1 }}
                    title="Finalise and purchase"
                    titleButtonText="Save for later"
                    titleButtonOnClick={sceneDrawer.viewActions.toShare}
                    titleButtonDisabled={
                        !itemBundling.bundles.length && !itemBundling.singleItems.length
                    }
                    confirmationBarContents={
                        <DrawerCtaContentBasket.Component
                            onFinaliseClick={() => {
                                if (conversionId !== undefined) {
                                    props.proceedToCart(
                                        {
                                            conversionId,
                                            products: cart.map(({ productId, quantity }) => ({
                                                id: productId,
                                                quantity,
                                            })),
                                            price: totalPrice,
                                        },
                                        'finalise step',
                                    );
                                    appControl.closeApp();
                                } else {
                                    throw new Error(
                                        'Proceed to Cart called before conversion ID generated',
                                    );
                                }
                            }}
                            onArClick={enterAr}
                            products={
                                itemBundling.bundles
                                    .map(b => productBySku?.(b.canonicalId))
                                    .concat(
                                        ...itemBundling.singleItems.map(i => i.products),
                                    ) as Product[]
                            }
                            totalPrice={Price.toLocaleString(totalPrice, locale, currency)}
                            platform={props.platform}
                        />
                    }
                >
                    <CheckoutDrawer.Component
                        items={itemBundling.singleItems}
                        bundles={itemBundling.bundles}
                        products={
                            itemBundling.bundles
                                .map(b => productBySku?.(b.canonicalId))
                                .concat(
                                    ...itemBundling.singleItems.map(i => i.products),
                                ) as Product[]
                        }
                        originalTotal={originalTotal}
                        singleItemsPrice={singleItemsPrice}
                        onAddClick={sceneDrawer.viewActions.toCatalogue}
                    />
                </DrawerWithCta.Component>
                <ArView.Component
                    platform={props.platform}
                    portrait={props.portrait}
                    open={sceneDrawer.currentView === 'ar-mobile-qr'}
                    onClose={sceneDrawer.viewActions.reset}
                    sx={{ zIndex: theme.zIndex.appBar + 1 }}
                    sharingUrlRequest={sharingUrlRequest}
                    img="assets/ARiPhone.png"
                />
                <ConfigureModaSofa.Component
                    sx={{
                        zIndex: theme.zIndex.appBar - 1,
                    }}
                    paddingTop={appBarHeight}
                    title={sceneDrawer.title}
                    platform={props.platform}
                    portrait={props.portrait}
                    open={sceneDrawer.currentView === 'configure'}
                    onClose={sceneDrawer.viewActions.reset}
                    currentConfiguration={sceneDrawer.currentSofaConfiguration}
                    range={range ?? 'Custom'}
                    loading={sofaLoading}
                    handleChange={(sofaConfiguration, oldSofaId) => {
                        dispatch(Navigation.modularSofaUpdatedAction());
                        setSofaLoading(true);
                        pipe(
                            aryaRef,
                            O.map(arya => {
                                const pose = pipe(
                                    oldSofaId,
                                    O.chain(sofaId => arya.furniturePose(sofaId)),
                                    O.getOrElse(() => Pose.identity()),
                                );

                                arya.addModularSofa(
                                    sofaConfiguration,
                                    pose,
                                    range ?? '',
                                    'user_add_item',
                                )
                                    .then(sofaId => {
                                        if (sofaId) {
                                            alert.open('Sofa Added');
                                            O.toNullable(aryaRef)?.selectFurniture(sofaId);
                                            setSofaLoading(false);
                                            sceneDrawer.viewActions.toSceneList();
                                        }
                                    })
                                    .catch(reason =>
                                        // TODO: Handle this
                                        dispatch(
                                            Logging.logError(
                                                `Modular sofa failed to add, todo: handle this in the UI.`,
                                                {
                                                    originalError: reason,
                                                },
                                            ),
                                        ),
                                    );

                                // runs alongside adding the group to avoid seeing both
                                // sofas at once
                                pipe(
                                    oldSofaId,
                                    O.map(x => arya.removeModularSofa(x)),
                                );
                            }),
                        );
                    }}
                />
                <AddItemsDrawer.Component
                    platform={props.platform}
                    portrait={props.portrait}
                    open={sceneDrawer.currentView === 'catalogue'}
                    sx={{ zIndex: theme.zIndex.appBar - 1 }}
                    paddingTop={appBarHeight}
                    onClose={sceneDrawer.viewActions.reset}
                    includeModularSofaCta={rangeSupportsModularSofas}
                    onModularSofaStart={() => {
                        // perhaps actual navigation should be done via the navigation dispatches?
                        // Or, these dispatches should happen as part of the viewAction.
                        dispatch(Navigation.modularSofaCreateAction());
                        sceneDrawer.viewActions.toConfigure();
                    }}
                    modularSofaImageUrl="assets/build-your-sofa.png"
                    products={catalogueEntries.map(c =>
                        PC.fromCatalogueEntry(c, p => Price.toLocaleString(p, locale, currency)),
                    )}
                    // TODO: update loadingFurnitureName & related to fit what this guy needs
                    loading={loadingFurnitureName !== ''}
                    loadingProductId={loadingFurnitureName}
                    // TODO: pull this out somewhere else
                    onProductAdd={id => {
                        const entry = PC.findCatalogueEntry(id, catalogueEntries);
                        if (entry) {
                            const { furnitureDescription, summary } = entry;
                            setLoadingFurnitureName(id);
                            O.toNullable(aryaRef)
                                ?.addFurniture(furnitureDescription, summary, 'user_add_item')
                                .then(furnitureId => {
                                    if (furnitureId) {
                                        O.toNullable(aryaRef)?.selectFurniture(furnitureId);
                                        setLoadingFurnitureId(furnitureId);
                                    }
                                })
                                .then(() => {
                                    sceneDrawer.viewActions.reset();
                                });
                        } else {
                            dispatch(
                                Logging.logWarning(
                                    'cannot add product: no matching catalogue entry found',
                                ),
                            );
                        }
                    }}
                />
                <SpaceDimensionsDrawer.Component
                    sx={{ zIndex: theme.zIndex.appBar - 1 }}
                    paddingTop={appBarHeight}
                    contentSx={{ height: `calc(100% - ${toPixels(appBarHeight)})` }}
                    open={sceneDrawer.currentView === 'floor-size'}
                    onCancel={sceneDrawer.viewActions.reset}
                    platform={props.platform}
                    portrait={props.portrait}
                    loading={spaceDimensionsUpdating}
                    onConfirm={(dimensions: SpaceDimensionsDrawer.Model) => {
                        setSpaceDimensionsUpdating(true);
                        const { width, length } = dimensions;

                        const newRoom: Room.Room = {
                            height: 0,
                            vertices: [
                                new Position2D(0, 0),
                                new Position2D(0, width),
                                new Position2D(length, width),
                                new Position2D(length, 0),
                            ],
                        };
                        O.toNullable(aryaRef)
                            ?.updateRoomDimensions(newRoom)
                            .then(() => {
                                setSpaceDimensionsUpdating(false);
                                alert.open('Floor Size Updated');
                                sceneDrawer.viewActions.reset();
                            });
                    }}
                />
                {
                    /** This component it used to load a series of
                     * product sets sequentially, downloading the AR
                     * GLB file for each */
                    props.environment === 'development' && automaticGlbDownloaderVisible ? (
                        <ProductSetGlbAutomator.Component
                            sceneLoading={sceneLoading}
                            exportGltf={name => {
                                pipe(
                                    aryaRef,
                                    O.map(a => a.generateFurnitureGLTF()),
                                    O.map(p =>
                                        p.then(gltf => {
                                            const blob = new Blob([gltf], {
                                                type: 'application/octet-binary',
                                            });

                                            const link = document.createElement('a');
                                            link.href = URL.createObjectURL(blob);
                                            link.download = `${
                                                itemBundling.bundles[0]?.canonicalId ?? name
                                            }.glb`;
                                            link.click();
                                        }),
                                    ),
                                );
                            }}
                        />
                    ) : null
                }
                {setContainsConfigurableProperties && (
                    <GlobalColourDrawer.Component
                        open={sceneDrawer.currentView === 'global-colour-config'}
                        closeDrawer={sceneDrawer.viewActions.reset}
                        loading={colourLoading}
                        unavailableColours={unavailable}
                        sx={{ zIndex: theme.zIndex.appBar - 1 }}
                    />
                )}
            </Box>
        </>
    );
}
